import { parseCsv } from "./csv";
import { resolveProfile } from "./resolve";
import type { Person } from "./model";
import type { Repository } from "./repository";

// A candidate person from an import: an identity pointer, context, and optional
// email/profile link. Imported people are UNTAGGED; importing creates no links
// to anyone.
export interface ContactCandidate {
  display_name: string;
  relationship_context: string;
  target_key: string;
  email?: string;
}

export interface ImportResult {
  added: number;
  skipped: number;
}

function slug(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

// Dedup / identity key from name + company, used when no profile URL is present.
export function identityKey(name: string, company: string): string {
  return `import:${slug(name)}|${slug(company)}`;
}

// Prefer the profile URL as the identity pointer ("linkedin:handle"); fall back
// to name+company when the export has no URL for a row.
function keyFor(url: string, name: string, company: string): string {
  const u = url.trim();
  if (u) {
    const resolved = resolveProfile(u);
    if (resolved.target_key) return resolved.target_key;
  }
  return identityKey(name, company);
}

// The header row is the first one carrying both "First Name" and "Last Name"
// (LinkedIn) — this also skips the export's preamble note lines.
function findLinkedInHeader(rows: string[][]): number {
  for (let i = 0; i < rows.length; i++) {
    const cells = rows[i]!.map((c) => c.trim().toLowerCase());
    if (cells.includes("first name") && cells.includes("last name")) return i;
  }
  return -1;
}

export function isLinkedInExport(text: string): boolean {
  return findLinkedInHeader(parseCsv(text)) >= 0;
}

// Parse a LinkedIn Connections.csv. Maps First+Last -> name, Position+Company ->
// context, the URL column -> a profile pointer, and the Email Address column ->
// email. Dedups within the file by the profile pointer (or name+company).
export function parseLinkedInConnections(text: string): ContactCandidate[] {
  const rows = parseCsv(text);
  const headerIdx = findLinkedInHeader(rows);
  if (headerIdx < 0) {
    throw new Error("This does not look like a LinkedIn Connections export.");
  }
  const header = rows[headerIdx]!.map((c) => c.trim().toLowerCase());
  const iFirst = header.indexOf("first name");
  const iLast = header.indexOf("last name");
  const iCompany = header.indexOf("company");
  const iPosition = header.indexOf("position");
  const iUrl = header.indexOf("url");
  const iEmail = header.indexOf("email address");

  const out: ContactCandidate[] = [];
  const seen = new Set<string>();
  for (let r = headerIdx + 1; r < rows.length; r++) {
    const cells = rows[r]!;
    const first = (cells[iFirst] ?? "").trim();
    const last = (cells[iLast] ?? "").trim();
    const name = `${first} ${last}`.trim();
    if (!name) continue;
    const company = iCompany >= 0 ? (cells[iCompany] ?? "").trim() : "";
    const position = iPosition >= 0 ? (cells[iPosition] ?? "").trim() : "";
    const context = [position, company].filter(Boolean).join(", ");
    const url = iUrl >= 0 ? (cells[iUrl] ?? "").trim() : "";
    const email = iEmail >= 0 ? (cells[iEmail] ?? "").trim() : "";
    const key = keyFor(url, name, company);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ display_name: name, relationship_context: context, target_key: key, email });
  }
  return out;
}

export interface GenericMapping {
  name: number;
  company?: number;
  position?: number;
}

// Build candidates from a generic CSV given a user-chosen column mapping. The
// first row is treated as the header.
export function parseGenericContacts(
  text: string,
  mapping: GenericMapping,
): ContactCandidate[] {
  const rows = parseCsv(text);
  const out: ContactCandidate[] = [];
  const seen = new Set<string>();
  for (let r = 1; r < rows.length; r++) {
    const cells = rows[r]!;
    const name = (cells[mapping.name] ?? "").trim();
    if (!name) continue;
    const company =
      mapping.company !== undefined ? (cells[mapping.company] ?? "").trim() : "";
    const position =
      mapping.position !== undefined ? (cells[mapping.position] ?? "").trim() : "";
    const context = [position, company].filter(Boolean).join(", ");
    const key = identityKey(name, company);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ display_name: name, relationship_context: context, target_key: key });
  }
  return out;
}

// An import row whose identity pointer already matches an existing person.
export interface Collision {
  candidate: ContactCandidate;
  existing: Person;
}

// Split candidates into those new to the list and those that collide with an
// existing person (so the user can resolve same-vs-different).
export async function partitionImport(
  repo: Repository,
  candidates: ContactCandidate[],
): Promise<{ fresh: ContactCandidate[]; collisions: Collision[] }> {
  const existing = await repo.listPeople();
  const byKey = new Map(existing.map((p) => [p.target_key, p]));
  const fresh: ContactCandidate[] = [];
  const collisions: Collision[] = [];
  for (const c of candidates) {
    const hit = byKey.get(c.target_key);
    if (hit) collisions.push({ candidate: c, existing: hit });
    else fresh.push(c);
  }
  return { fresh, collisions };
}

export async function addFreshContacts(
  repo: Repository,
  fresh: ContactCandidate[],
): Promise<number> {
  for (const c of fresh) {
    await repo.addPerson({
      display_name: c.display_name,
      relationship_context: c.relationship_context,
      target_key: c.target_key,
      email: c.email,
      source: "import",
    });
  }
  return fresh.length;
}

// Add a colliding candidate the user marked as a DIFFERENT person: dropping the
// shared profile pointer (a distinct person cannot share one profile URL).
export async function addContactAsNew(
  repo: Repository,
  c: ContactCandidate,
): Promise<void> {
  await repo.addPerson({
    display_name: c.display_name,
    relationship_context: c.relationship_context,
    email: c.email,
    source: "import",
  });
}

// Add new candidates as untagged people, skipping any already present (by the
// name+company identity key). Never tags.
export async function importContacts(
  repo: Repository,
  candidates: ContactCandidate[],
): Promise<ImportResult> {
  const existing = await repo.listPeople();
  const keys = new Set(existing.map((p) => p.target_key));
  let added = 0;
  let skipped = 0;
  for (const c of candidates) {
    if (keys.has(c.target_key)) {
      skipped++;
      continue;
    }
    await repo.addPerson({
      display_name: c.display_name,
      relationship_context: c.relationship_context,
      target_key: c.target_key,
      email: c.email,
      source: "import",
    });
    keys.add(c.target_key);
    added++;
  }
  return { added, skipped };
}
